import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashCan, faUsers } from '@fortawesome/free-solid-svg-icons';

import { IMyProfile } from '@share/common-types';
import { getMyProfile, ILoginState, IMyProfileState } from '@share/store/slices';
import { MyProfileAPI } from '@share/api';
import { FunctionalRootState, RootState, Toaster } from '@share/utils';

import { ModalConfirmation } from '../../../common/modal-confirmation';
import { ModalAddAuthorizedUser } from '../../../my-profile/modal-add-authorize-user';
import { AccountEmptyState } from '../empty-state';

import styles from './styles.module.scss';

const getFullName = (person: IMyProfile): string =>
  [person.firstName, person.middleName, person.lastName].filter(Boolean).join(' ');

const getInitials = (person: IMyProfile): string =>
  [person.firstName, person.lastName].map(part => (part || '').trim().charAt(0)).join('').toUpperCase();

// Only users added from the site (numeric AuthId references) can be removed here, as in the legacy tab.
const isRemovable = (person: IMyProfile): boolean => person?.referenceType === 'AuthId' && /^\d+$/.test(person.referenceId);

/** Authorized users as a list of people, with add and remove reusing the legacy modal and API. */
export const AccountAuthorizedUsers = (): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();

  const loginStore: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);
  const myProfileStore: IMyProfileState = useSelector((state: FunctionalRootState) => state.myProfileStore);

  const people: IMyProfile[] = myProfileStore?.result?.authorizedUsers || [];
  const rsiId = loginStore.user?.keyid as number;

  const [showAdd, setShowAdd] = useState(false);
  const [toRemove, setToRemove] = useState<IMyProfile | null>(null);
  const [removing, setRemoving] = useState(false);

  const handleConfirmRemove = () => {
    if (!toRemove || removing) {
      return;
    }
    setRemoving(true);
    MyProfileAPI.deleteAuthorizedUser(rsiId, parseInt(toRemove.referenceId, 10))
      .then(() => {
        setToRemove(null);
        dispatch(getMyProfile());
      })
      .catch((error: unknown) => {
        console.error('Account hub: removing an authorized user failed', error);
        Toaster.error(intl.formatMessage({ id: 'my.account.hub.authorized.remove_error' }));
      })
      .finally(() => setRemoving(false));
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionIntro}>
        <p className={styles.introText}>{intl.formatMessage({ id: 'my.account.hub.authorized.intro' })}</p>
        {people.length ? (
          <button type="button" className={styles.primaryButton} onClick={() => setShowAdd(true)}>
            <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
            {intl.formatMessage({ id: 'add.authorized.users' })}
          </button>
        ) : null}
      </div>

      {people.length ? (
        <ul className={styles.people}>
          {people.map(person => (
            <li key={`${person.referenceType}-${person.referenceId}`} className={styles.person}>
              <span className={styles.avatar} aria-hidden="true">{getInitials(person)}</span>
              <div className={styles.personText}>
                <div className={styles.personName}>{getFullName(person)}</div>
                <div className={styles.personMeta}>
                  {[person.personType, person.email].filter(Boolean).join(' · ')}
                </div>
              </div>
              {isRemovable(person) ? (
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={() => setToRemove(person)}
                  aria-label={intl.formatMessage({ id: 'my.account.hub.authorized.remove' }, { name: getFullName(person) })}
                >
                  <FontAwesomeIcon icon={faTrashCan} aria-hidden="true" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <AccountEmptyState
          variant="panel"
          icon={faUsers}
          title={intl.formatMessage({ id: 'my.account.hub.authorized.empty_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.authorized.empty_body' })}
          action={{ label: intl.formatMessage({ id: 'add.authorized.users' }), onClick: () => setShowAdd(true) }}
        />
      )}

      <ModalConfirmation
        message="confirm.delete.authorized.users"
        title="delete.authorized.users"
        yesMessage="confirm"
        noMessage="cancel"
        visible={!!toRemove}
        disabled={removing}
        onCancel={() => {
          if (!removing) {
            setToRemove(null);
          }
        }}
        onConfirm={handleConfirmRemove}
      />

      <ModalAddAuthorizedUser
        visible={showAdd}
        rsiId={rsiId}
        disabled={loginStore.loading || removing}
        onCancel={() => setShowAdd(false)}
      />
    </div>
  );
};
